import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AtsGetQuestionTemplatesFeatureDocument,
  AtsJobPostingSettingsCustomQuestionFragment,
} from 'generated/resources/graphql'
import { maxBy, orderBy } from 'lodash'

import SortableLinkBoxList from 'modules/core/components/SortableLinkBoxList'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { AddCustomQuestion } from './AddCustomQuestion'
import { AddFromTemplateQuestions } from './AddFromTemplateQuestions'
import { CustomQuestionsItem } from './CustomQuestionsItem'
import { SelectAddQuestionModal } from './SelectAddQuestionModal'
import { CustomQuestionsModalOptions } from './useCustomQuestion'
import { useReorderCustomQuestions } from './useReorderCustomQuestions'

interface Props {
  customQuestions: AtsJobPostingSettingsCustomQuestionFragment[]
  jobId: number
}

export const CustomQuestions = ({ customQuestions, jobId }: Props) => {
  const [showModal, setShowModal] = useState<CustomQuestionsModalOptions>()
  const { company } = useCurrentSession()

  const { data: questionTemplatesFeature, loading: featureLoading } = useQuery(
    AtsGetQuestionTemplatesFeatureDocument
  )

  const { reorder } = useReorderCustomQuestions()

  const onReorder = (data: AtsJobPostingSettingsCustomQuestionFragment[]) => {
    reorder(data.map((question, index) => ({ id: question.id, position: index })))
  }

  const lastPosition = maxBy(customQuestions, (cq) => cq.position)?.position

  if (featureLoading) {
    return null
  }

  const customizedFeatures =
    questionTemplatesFeature?.companies.featureAvailabilitiesConnection?.nodes.find(
      (feature) => feature.id === 'ats_customized_features'
    )?.available

  const canManageQuestionTemplates =
    questionTemplatesFeature?.ats._policies.manageAtsSettings.can && customizedFeatures

  return (
    <Box gap="s16">
      <Box gap="s8">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('ats.job_posting_settings.application_form.custom_questions.title')}
        </Text>
        <Text size="300" color="grey800">
          {i18n.t('ats.job_posting_settings.application_form.custom_questions.description')}
        </Text>
      </Box>

      <Button
        onClick={() => {
          if (customizedFeatures) {
            setShowModal('new')
          } else {
            setShowModal('fromScratch')
          }
        }}
        iconName="add"
        loading={featureLoading}
      >
        {i18n.t('common.add')}
      </Button>

      <Box paddingTop="s40">
        {customQuestions.length > 0 ? (
          <SortableLinkBoxList
            items={orderBy(customQuestions, 'position')}
            onReorder={onReorder}
            itemAsKey={(item) => item.id.toString()}
            renderItem={(item) => (
              <CustomQuestionsItem
                item={item}
                companyId={company.id}
                canSaveTemplates={canManageQuestionTemplates || false}
              />
            )}
          />
        ) : (
          <Text.Normal weight="medium" color="grey800" ellipsis>
            {i18n.t(
              'ats.job_posting_settings.application_form.custom_questions.empty_questions_list'
            )}
          </Text.Normal>
        )}
      </Box>

      {customizedFeatures && (
        <>
          {showModal === 'new' && (
            <SelectAddQuestionModal
              onClose={() => setShowModal(undefined)}
              onSelectOption={(option: CustomQuestionsModalOptions) => setShowModal(option)}
            />
          )}
          {showModal === 'fromTemplates' && (
            <AddFromTemplateQuestions onClose={() => setShowModal(undefined)} jobId={jobId} />
          )}
        </>
      )}
      {showModal === 'fromScratch' && (
        <AddCustomQuestion
          onClose={() => setShowModal(undefined)}
          position={(lastPosition || customQuestions.length) + 1}
          jobId={jobId}
          companyId={company.id}
        />
      )}
    </Box>
  )
}
